"use client";

import { c as _c } from "react/compiler-runtime";
import { Select as SelectPrimitive } from "@base-ui/react/select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon } from "lucide-react";
import * as React from "react";
import { fieldErrorClassName, fieldLabelRowClassName, fieldOptionalMarkerClassName, fieldWrapperClassName } from "@/components/ui/form-field-styles";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/** Popup surface shared by `SelectContent` and `AutocompleteContent`. */
export const selectPopupClassName = "bg-popover text-popover-foreground ring-border data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 relative isolate z-50 max-h-64 w-(--anchor-width) min-w-32 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg shadow-md ring-1 duration-100 data-[align-trigger=true]:animate-none";

/** Option row shared by `SelectItem` and `AutocompleteItem`. */
export const selectItemClassName = "focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground relative flex min-h-8 w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2";
const SelectTriggerIdContext = React.createContext<string | undefined>(undefined);
const SelectErrorIdContext = React.createContext<string | undefined>(undefined);
function Select(t0) {
  const $ = _c(27);
  let error;
  let id;
  let isLoading;
  let isOptional;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      id,
      label,
      error,
      isLoading,
      isOptional,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = error;
    $[2] = id;
    $[3] = isLoading;
    $[4] = isOptional;
    $[5] = label;
    $[6] = props;
  } else {
    error = $[1];
    id = $[2];
    isLoading = $[3];
    isOptional = $[4];
    label = $[5];
    props = $[6];
  }
  const generatedId = React.useId();
  const selectId = id ?? (label || error ? generatedId : undefined);
  const t1 = error ? `${selectId}-error` : undefined;
  let t2;
  if ($[7] !== props) {
    t2 = <SelectPrimitive.Root data-slot="select" {...props} />;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <SelectErrorIdContext.Provider value={t1}>{t2}</SelectErrorIdContext.Provider>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== selectId || $[13] !== t3) {
    t4 = <SelectTriggerIdContext.Provider value={selectId}>{t3}</SelectTriggerIdContext.Provider>;
    $[12] = selectId;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const select = t4;
  if (!label && !error && !isLoading && !isOptional) {
    return select;
  }
  let t5;
  if ($[15] !== isLoading || $[16] !== isOptional || $[17] !== label || $[18] !== selectId) {
    t5 = label && <div className={fieldLabelRowClassName}><div className="flex items-center gap-2"><Label htmlFor={selectId}>{label}</Label>{isLoading && <span className={fieldOptionalMarkerClassName}>Carregando...</span>}</div>{isOptional && <span className={fieldOptionalMarkerClassName}>Opcional</span>}</div>;
    $[15] = isLoading;
    $[16] = isOptional;
    $[17] = label;
    $[18] = selectId;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== error || $[21] !== selectId) {
    t6 = error && <div className={fieldErrorClassName} id={`${selectId}-error`}><InfoIcon aria-hidden="true" className="size-3.5" />{error}</div>;
    $[20] = error;
    $[21] = selectId;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== select || $[24] !== t5 || $[25] !== t6) {
    t7 = <div className={fieldWrapperClassName}>{t5}{select}{t6}</div>;
    $[23] = select;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function SelectGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("scroll-my-1 p-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Group data-slot="select-group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectValue(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <SelectPrimitive.Value data-slot="select-value" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SelectTrigger(t0) {
  const $ = _c(20);
  let children;
  let className;
  let id;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      children,
      id,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = id;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    id = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? "default" : t1;
  const contextId = React.useContext(SelectTriggerIdContext);
  const errorId = React.useContext(SelectErrorIdContext);
  const t2 = id ?? contextId;
  const t3 = Boolean(errorId) || props["aria-invalid"];
  let t4;
  if ($[6] !== errorId || $[7] !== props) {
    t4 = [props["aria-describedby"], errorId].filter(Boolean).join(" ") || undefined;
    $[6] = errorId;
    $[7] = props;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== className) {
    t5 = cn("border-input bg-input/20 focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-placeholder:text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 flex w-full items-center justify-between gap-1.5 rounded-md border px-3 py-2 text-sm whitespace-nowrap transition-colors outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-2 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className);
    $[9] = className;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SelectPrimitive.Icon render={<ChevronDownIcon className="text-muted-foreground pointer-events-none size-4" />} />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== props || $[14] !== size || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t7 = <SelectPrimitive.Trigger {...props} data-slot="select-trigger" data-size={size} id={t2} aria-invalid={t3} aria-describedby={t4} className={t5}>{children}{t6}</SelectPrimitive.Trigger>;
    $[12] = children;
    $[13] = props;
    $[14] = size;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function SelectContent(t0) {
  const $ = _c(27);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      className,
      children,
      side: t1,
      sideOffset: t2,
      align: t3,
      alignOffset: t4,
      alignItemWithTrigger: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  const side = t1 === undefined ? "bottom" : t1;
  const sideOffset = t2 === undefined ? 4 : t2;
  const align = t3 === undefined ? "start" : t3;
  const alignOffset = t4 === undefined ? 0 : t4;
  const alignItemWithTrigger = t5 === undefined ? false : t5;
  let t6;
  if ($[9] !== className) {
    t6 = cn(selectPopupClassName, className);
    $[9] = className;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SelectScrollUpButton />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== children) {
    t8 = <SelectPrimitive.List className="p-1">{children}</SelectPrimitive.List>;
    $[12] = children;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SelectScrollDownButton />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== alignItemWithTrigger || $[16] !== props || $[17] !== t6 || $[18] !== t8) {
    t10 = <SelectPrimitive.Popup data-slot="select-content" data-align-trigger={alignItemWithTrigger} className={t6} {...props}>{t7}{t8}{t9}</SelectPrimitive.Popup>;
    $[15] = alignItemWithTrigger;
    $[16] = props;
    $[17] = t6;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== align || $[21] !== alignItemWithTrigger || $[22] !== alignOffset || $[23] !== side || $[24] !== sideOffset || $[25] !== t10) {
    t11 = <SelectPrimitive.Portal><SelectPrimitive.Positioner align={align} alignOffset={alignOffset} alignItemWithTrigger={alignItemWithTrigger} side={side} sideOffset={sideOffset} className="isolate z-50">{t10}</SelectPrimitive.Positioner></SelectPrimitive.Portal>;
    $[20] = align;
    $[21] = alignItemWithTrigger;
    $[22] = alignOffset;
    $[23] = side;
    $[24] = sideOffset;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function SelectLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground px-2 py-1.5 text-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.GroupLabel data-slot="select-label" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectItem(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(selectItemClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children) {
    t2 = <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">{children}</SelectPrimitive.ItemText>;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectPrimitive.ItemIndicator render={<span className="pointer-events-none absolute right-2 flex items-center justify-center" />}><CheckIcon className="pointer-events-none" /></SelectPrimitive.ItemIndicator>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t1 || $[11] !== t2) {
    t4 = <SelectPrimitive.Item data-slot="select-item" className={t1} {...props}>{t2}{t3}</SelectPrimitive.Item>;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function SelectSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-border/50 pointer-events-none -mx-1 my-1 h-px", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Separator data-slot="select-separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectScrollUpButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-popover top-0 z-10 flex w-full cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-3.5", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUpIcon />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollUpArrow data-slot="select-scroll-up-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollUpArrow>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SelectScrollDownButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-popover bottom-0 z-10 flex w-full cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-3.5", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDownIcon />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollDownArrow data-slot="select-scroll-down-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollDownArrow>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };